import React, { useState } from 'react';
import { Loader2, Lock, MessageCircle } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { CommentForm } from '../../discussion/CommentForm';
import { CommentList } from '../../discussion/CommentList';
import { UserModal } from './UserModal';
import { useAuth } from '../../../hooks/useAuth';
import { useCategoryGame } from '../../../contexts/GameContext';
import { useDiscussionComments } from '../../../hooks/useDiscussionComments';
import { StockCategoryFilter } from '../../../types/stockCategories';

interface DiscussionModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockSymbol: string | null;
  category?: StockCategoryFilter;
  gameDate?: string;
  className?: string;
}

/**
 * DiscussionModal - Modal for discussing daily stocks
 * Features:
 * - Comment count display
 * - Auth-gated comment form
 * - Nested comment tree
 * - Loading and error states
 * - Empty state message
 */
export const DiscussionModal: React.FC<DiscussionModalProps> = ({
  isOpen,
  onClose,
  stockSymbol,
  category,
  gameDate,
  className
}) => {
  const { user } = useAuth();
  const { gameState } = useCategoryGame();
  const [showUserModal, setShowUserModal] = useState(false);

  // Check if user has completed the game for this category
  const hasPlayedGame = gameState.isGameComplete;

  const {
    comments,
    totalCount,
    isLoading,
    error,
    postComment,
    voteComment
  } = useDiscussionComments({
    stockSymbol,
    category,
    gameDate,
    enabled: isOpen
  });

  const handlePostComment = async (text: string) => {
    return await postComment(text);
  };

  const handleReply = async (text: string, parentId: string) => {
    return await postComment(text, parentId);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Discussion"
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <div className="space-y-4">
          {/* Comment Count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {totalCount} {totalCount === 1 ? 'comment' : 'comments'}
            </p>
          </div>

          {/* Comment Form (Auth + Game Completion Required) */}
          {!user ? (
            <div className="border rounded-lg p-6 text-center bg-muted/50">
              <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                Sign in to join the discussion
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Share your insights and see what others think about this stock
              </p>
              <Button onClick={() => setShowUserModal(true)}>
                Sign In
              </Button>
            </div>
          ) : !hasPlayedGame ? (
            <div className="border rounded-lg p-6 text-center bg-muted/50">
              <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                Complete today's game to join the discussion
              </h3>
              <p className="text-sm text-muted-foreground">
                Finish playing to see what others think and share your insights
              </p>
            </div>
          ) : (
            <div className="pb-4 border-b border-border">
              <CommentForm
                onSubmit={handlePostComment}
                placeholder="Share your thoughts on this stock..."
                submitLabel="Post Comment"
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Loading comments...</p>
            </div>
          ) : !hasPlayedGame ? (
            /* Hide comment list if user hasn't played */
            null
          ) : comments.length === 0 ? (
            /* Empty State */
            <div className="text-center py-12">
              <MessageCircle className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold mb-2">
                No comments yet
              </h3>
              <p className="text-sm text-muted-foreground">
                Be the first to discuss this stock!
              </p>
            </div>
          ) : (
            /* Comment List */
            <CommentList
              comments={comments}
              onVote={voteComment}
              onReply={handleReply}
            />
          )}
        </div>
      </Modal>

      {/* User Modal (Sign In) */}
      <UserModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
      />
    </>
  );
};
